// Shared reactive-token-refresh policy used by every frontend API client.
//
// The app already refreshes tokens (MSAL acquireTokenSilent runs per request),
// but a token that reaches the backend expired/invalid is rejected mid-session.
// When that happens a client should force a genuinely fresh token and retry the
// request once, and only sign the user out if it still fails. This module is
// the single source of truth for "is this failure a token problem we should try
// to recover from?" so every client agrees on the answer.

/**
 * Sentinel access token returned by the dev-bypass provider instead of a real
 * JWT. Clients skip Bearer attachment — and skip refresh/retry — when they see
 * it, because there is no token to refresh in bypass mode.
 */
export const DEV_TOKEN = 'dev';

/**
 * Decides whether an HTTP auth failure should trigger a forced token refresh
 * and a single retry.
 *
 * Recoverable (→ force-refresh + retry once):
 *   - 401 — the gateway rejected a missing/invalid Authorization header.
 *   - 403 whose error-body `code` is NOT "forbidden" — the API Gateway
 *     authorizer rejects an expired/invalid token with a 403 that carries the
 *     default `{"message":"Forbidden"}` body (no `code`).
 *
 * NOT recoverable (surface as a normal error, never refresh, never sign out):
 *   - 403 with `code === "forbidden"` — a genuine RBAC denial emitted by the
 *     backend via httpx (`CodeForbidden`). Refreshing the token cannot grant a
 *     permission the user lacks, and retrying would wrongly log them out.
 *
 * @param status HTTP status code of the response.
 * @param code   Machine-readable `code` field parsed from the JSON error body,
 *               if present.
 */
export function isAuthRecoverable(status: number, code?: string): boolean {
  if (status === 401) return true;
  if (status === 403) return code !== 'forbidden';
  return false;
}
